import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button, Input, Select, Empty, Badge, Textarea } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function Announcements() {
    const { activeClubId } = useAuth();
    const [items, setItems] = useState([]);
    const { open, openModal, closeModal } = useModal();
    const empty = { title: "", content: "", target_audience: "all", is_published: true };
    const [form, setForm] = useState(empty);

    const load = () => {
        if (!activeClubId) return;
        api.get("/comms/announcements", { params: { club_id: activeClubId } }).then((r) => setItems(r.data));
    };
    useEffect(load, [activeClubId]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/comms/announcements", form, { params: { club_id: activeClubId } });
            toast.success("Annonce publiée");
            closeModal();
            setForm(empty);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette annonce ?")) return;
        await api.delete(`/comms/announcements/${id}`, { params: { club_id: activeClubId } });
        load();
    };

    return (
        <div data-testid="announcements-page">
            <PageHeader
                title="Annonces internes"
                subtitle="Communiquez avec coachs, parents, joueurs et staff"
                action={<Button onClick={openModal} data-testid="add-announcement-button"><Plus className="w-4 h-4" /> Nouvelle annonce</Button>}
            />

            {items.length === 0 ? (
                <Empty title="Aucune annonce" hint="Publiez votre première annonce." />
            ) : (
                <div className="space-y-3">
                    {items.map((a) => (
                        <Card key={a.id}>
                            <CardBody>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Megaphone className="w-4 h-4 text-orange-500" />
                                            <h3 className="font-display font-bold text-slate-900">{a.title}</h3>
                                            <Badge color="blue">{a.target_audience}</Badge>
                                        </div>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.content}</p>
                                        <div className="text-xs text-slate-400 mt-2">{a.created_at?.slice(0, 16).replace("T", " ")}</div>
                                    </div>
                                    <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={closeModal} title="Nouvelle annonce">
                <form onSubmit={submit} className="space-y-4" data-testid="announcement-form">
                    <Input label="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    <Textarea label="Contenu" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
                    <Select label="Audience" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}>
                        <option value="all">Tous</option>
                        <option value="coaches">Coachs</option>
                        <option value="parents">Parents</option>
                        <option value="players">Joueurs</option>
                        <option value="staff">Staff</option>
                    </Select>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit" data-testid="announcement-submit">Publier</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
